import { WORLD_HEIGHT, WORLD_WIDTH } from "./constants";
import { GameLoop } from "./GameLoop";
import { isNewRecord } from "./rules";
import { confettiRain, Particle } from "../entities/Particle";
import { CharacterScene } from "../scenes/CharacterScene";
import { GameOverScene } from "../scenes/GameOverScene";
import { InstructionsScene } from "../scenes/InstructionsScene";
import { MainMenuScene } from "../scenes/MainMenuScene";
import { PauseScene } from "../scenes/PauseScene";
import { PlayScene } from "../scenes/PlayScene";
import { TutorialScene } from "../scenes/TutorialScene";
import type { Scene } from "../scenes/Scene";
import { AudioSystem } from "../systems/AudioSystem";
import { InputSystem } from "../systems/InputSystem";
import { LocalizationSystem } from "../systems/LocalizationSystem";
import { PlayController } from "../systems/PlayController";
import { SaveSystem } from "../systems/SaveSystem";
import { WorldRenderer } from "../systems/WorldRenderer";
import { UiRoot, type UiScreen } from "../ui/UiRoot";
import { PWA_ENABLED } from "../config/variant";
import { isStandaloneDisplay, resolveInstallUiState, shouldListenForInstallPrompt } from "../pwa/policy";
import type { DifficultyId, Language, SceneId, Vec2 } from "./types";

type InstallPrompt = Event & { prompt: () => Promise<void> };

export class Game {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly loop: GameLoop;
  private readonly save: SaveSystem;
  private readonly i18n = new LocalizationSystem();
  private readonly audio = new AudioSystem();
  private readonly input = new InputSystem();
  private readonly world = new WorldRenderer();
  private readonly ui: UiRoot;
  private readonly scenes: Record<"menu" | "character" | "instructions" | "tutorial" | "play" | "pause" | "gameover", Scene>;
  private play: PlayController | null = null;
  private scene: SceneId = "menu";
  private bgTime = 0;
  private countdown = 0;
  private portrait = false;
  private installEvent: InstallPrompt | null = null;
  private ended = false;
  private celebration: Particle[] = [];

  constructor(
    private readonly canvas: HTMLCanvasElement,
    uiRoot: HTMLElement,
    hud: HTMLElement,
    rotate: HTMLElement,
    storage: Storage,
  ) {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas 2D indisponible");
    this.ctx = ctx;
    this.save = new SaveSystem(storage);
    this.i18n.setLanguage(this.save.data.language);
    this.audio.setVolumes(this.save.data.volumes);
    this.audio.setReduceMotion(this.save.data.accessibility.reduceMotion);
    this.ui = new UiRoot(uiRoot, hud, rotate, this.i18n, this.save, {
      play: () => void this.onPlay(false),
      playTutorial: () => void this.onPlay(true),
      open: (screen) => this.openScreen(screen),
      resume: () => this.resume(),
      restart: () => void this.beginMatch(Boolean(this.play?.tutorial && this.play.tutorial !== "done")),
      toMenu: () => this.toMenu(),
      skipTutorial: () => this.onSkipTutorial(),
      tutorialNext: () => {
        this.play?.advanceTutorial();
        if (this.play?.tutorial === "done") {
          this.scene = "play";
          document.body.dataset.scene = "play";
        }
      },
      setLanguage: (language) => this.setLanguage(language),
      setDifficulty: (id) => {
        this.save.setDifficulty(id);
        this.ui.render();
      },
      setNickname: (name) => {
        const character = { ...this.save.data.character, nickname: name };
        this.save.setCharacter(character);
      },
      saveCharacter: (config) => {
        this.save.setCharacter(config);
        this.go("menu");
      },
      toggleFullscreen: () => void this.toggleFullscreen(),
      install: () => void this.install(),
      setVolume: (kind, value) => {
        void this.audio.unlock();
        const volumes = { ...this.save.data.volumes, [kind]: value };
        this.save.setVolumes(volumes);
        this.audio.setVolumes(volumes);
      },
      setAccessibility: () => {
        this.audio.setReduceMotion(this.save.data.accessibility.reduceMotion);
        this.ui.applyChrome();
      },
      clearScores: () => this.save.clearScores(),
      resetAll: () => {
        this.save.resetAll();
        this.i18n.setLanguage(this.save.data.language);
        this.audio.setVolumes(this.save.data.volumes);
        this.ui.applyChrome();
      },
    });
    this.scenes = {
      menu: new MainMenuScene(this.ui),
      character: new CharacterScene(this.ui),
      instructions: new InstructionsScene(this.ui),
      tutorial: new TutorialScene(this.ui),
      play: new PlayScene(this.ui),
      pause: new PauseScene(this.ui),
      gameover: new GameOverScene(this.ui),
    };
    this.loop = new GameLoop((dt) => this.tick(dt));
    this.bind();
    this.syncInstallUi();
    this.resize();
    this.go("menu");
    this.loop.start();
  }

  dispose(): void {
    this.loop.stop();
    this.input.detach();
    this.audio.dispose();
    window.removeEventListener("resize", this.resize);
    window.removeEventListener("blur", this.onBlur);
    document.removeEventListener("visibilitychange", this.onVisibility);
    if (shouldListenForInstallPrompt(PWA_ENABLED)) {
      window.removeEventListener("beforeinstallprompt", this.onBeforeInstall);
      window.removeEventListener("appinstalled", this.onAppInstalled);
    }
  }

  private bind(): void {
    window.addEventListener("resize", this.resize);
    window.addEventListener("orientationchange", this.resize);
    window.addEventListener("blur", this.onBlur);
    document.addEventListener("visibilitychange", this.onVisibility);
    if (shouldListenForInstallPrompt(PWA_ENABLED)) {
      window.addEventListener("beforeinstallprompt", this.onBeforeInstall);
      window.addEventListener("appinstalled", this.onAppInstalled);
    }
    document.addEventListener("fullscreenchange", () => {
      this.ui.fullscreen = Boolean(document.fullscreenElement);
      if (this.scene === "menu" || this.scene === "pause" || this.scene === "options") this.ui.render();
    });
    this.canvas.addEventListener("contextmenu", (event) => event.preventDefault());
    this.canvas.addEventListener("touchmove", (event) => event.preventDefault(), { passive: false });
  }

  private onBeforeInstall = (event: Event): void => {
    event.preventDefault();
    this.installEvent = event as InstallPrompt;
    this.syncInstallUi();
    this.ui.render();
  };

  private onAppInstalled = (): void => {
    this.installEvent = null;
    this.syncInstallUi();
    this.ui.render();
  };

  private syncInstallUi(): void {
    const matches = (query: string): boolean => window.matchMedia(query).matches;
    const iosStandalone = "standalone" in navigator && Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
    this.ui.installUi = resolveInstallUiState({
      pwaEnabled: PWA_ENABLED,
      canPrompt: Boolean(this.installEvent),
      installed: isStandaloneDisplay(matches, iosStandalone),
    });
  }

  private resize = (): void => {
    this.portrait = window.innerHeight > window.innerWidth;
    this.ui.renderRotate(this.portrait);
    const dpr = Math.min(window.devicePixelRatio || 1, 2.5);
    this.canvas.width = Math.round(WORLD_WIDTH * dpr);
    this.canvas.height = Math.round(WORLD_HEIGHT * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (this.portrait && this.scene === "play") this.pause(true);
  };

  private tick(dt: number): void {
    const acc = this.save.data.accessibility;
    if (!this.portrait) this.bgTime += dt * 1000;
    const playing = this.scene === "play" || this.scene === "tutorial";
    this.audio.update(dt);
    if (this.countdown > 0) {
      const before = Math.ceil(this.countdown);
      this.countdown -= dt;
      const after = Math.max(1, Math.ceil(this.countdown));
      if (after !== before) this.ui.setCountdown(after);
      if (this.countdown <= 0) {
        this.countdown = 0;
        if (this.play) this.play.state.frozen = false;
        this.audio.enterPlay();
        this.ui.show("none");
      }
    }
    const showPlay = Boolean(this.play) && (playing || this.scene === "pause" || this.scene === "help" || this.countdown > 0);
    if (this.play && showPlay && !this.portrait) {
      if (playing && this.countdown <= 0) {
        this.play.update(dt, this.input.aiming, this.input.pointer);
        if (this.input.aiming) this.play.startAim();
      }
      this.play.draw(this.ctx, playing && this.input.aiming && this.countdown <= 0, this.input.pointer);
      if (playing) this.ui.renderHud(this.play, this.ui.tutorialKey(this.play.tutorial));
      if (this.play.match.gameOver && !this.ended) this.endMatch();
    } else {
      const slow = this.scene === "gameover" ? (acc.reduceMotion ? 0 : 0.22) : 1;
      this.world.update(dt * slow, acc.reduceMotion);
      this.world.draw(this.ctx, this.bgTime, acc.reduceMotion, acc.highContrast);
      if (this.scene === "gameover") {
        for (const p of this.celebration) {
          if (acc.reduceMotion) p.life -= dt * 0.6;
          else p.update(dt);
        }
        this.celebration = this.celebration.filter((p) => p.alive);
        for (const p of this.celebration) p.draw(this.ctx);
      }
      if (this.scene === "character") this.ui.paintCharacterPreview(this.bgTime / 1000);
    }
  }

  private async onPlay(forceTutorial: boolean): Promise<void> {
    void Promise.race([
      this.audio.unlock().catch(() => undefined),
      new Promise((resolve) => window.setTimeout(resolve, 400)),
    ]).then(() => this.audio.play("button"));
    if (!forceTutorial && this.scene === "menu" && !this.save.data.tutorialSeen) {
      this.go("instructions");
      return;
    }
    void this.requestPlayFullscreen();
    await this.beginMatch(forceTutorial);
  }

  private async beginMatch(tutorial: boolean): Promise<void> {
    this.ended = false;
    this.celebration = [];
    this.play = new PlayController(this.save.data.difficulty, this.save, this.audio, this.world, tutorial);
    this.input.attach(this.canvas, (point: Vec2) => this.play?.throwAt(point));
    this.ui.invalidateHud();
    this.go(tutorial ? "tutorial" : "play");
    this.play.state.frozen = false;
    void this.audio.unlock().then(() => this.audio.enterPlay());
  }

  private onSkipTutorial(): void {
    this.audio.play("button");
    if (this.scene === "instructions") {
      this.save.markTutorialSeen();
      void this.requestPlayFullscreen();
      void this.beginMatch(false);
      return;
    }
    this.play?.skipTutorial();
    this.scene = "play";
    document.body.dataset.scene = "play";
    this.ui.invalidateHud();
  }

  private openScreen(screen: UiScreen): void {
    this.audio.play("button");
    if (screen === "pause") this.pause(false);
    else if (screen === "help") {
      this.ui.show("help");
    } else if (screen === "character" || screen === "instructions" || screen === "scores" || screen === "options") {
      this.go(screen);
    }
  }

  private pause(auto: boolean): void {
    if (!this.play || this.play.match.gameOver) return;
    if (this.scene !== "play" && this.scene !== "tutorial" && this.scene !== "pause") return;
    this.play.state.frozen = true;
    this.countdown = 0;
    this.scene = "pause";
    this.scenes.pause.enter();
    this.ui.hideHud();
    this.audio.enterPause();
    void auto;
  }

  private resume(): void {
    if (!this.play) return;
    this.audio.play("button");
    this.scene = this.play.tutorial && this.play.tutorial !== "done" ? "tutorial" : "play";
    this.play.state.frozen = true;
    this.countdown = 3;
    this.ui.invalidateHud();
    this.ui.show("countdown");
    this.ui.setCountdown(3);
  }

  private async endMatch(): Promise<void> {
    if (!this.play || this.ended) return;
    this.ended = true;
    const entry = this.play.scoreEntry();
    const previous = this.save.data.scores[this.play.match.difficulty];
    const record = isNewRecord(previous, entry.score);
    this.audio.play(record ? "gameOverRecord" : "gameOver");
    this.audio.enterMenu();
    this.input.detach();
    const board = this.save.addScore(this.play.match.difficulty, entry);
    this.celebration = confettiRain(record, this.save.data.accessibility.reduceMotion);
    this.go("gameover");
    this.ui.showGameOver(this.play.match, board, record);
  }

  private toMenu(): void {
    this.audio.play("button");
    this.audio.enterMenu();
    this.input.detach();
    this.play = null;
    this.ended = false;
    this.countdown = 0;
    this.celebration = [];
    this.go("menu");
  }

  private go(id: SceneId): void {
    this.scene = id;
    document.body.dataset.scene = id;
    const scene = this.scenes[id as keyof typeof this.scenes];
    scene?.enter();
    if (id === "scores" || id === "options") this.ui.show(id);
  }

  private setLanguage(language: Language): void {
    this.save.setLanguage(language);
    this.i18n.setLanguage(language);
    this.audio.play("button");
    this.ui.render();
  }

  private onBlur = (): void => {
    if (this.scene === "play" || this.scene === "tutorial") this.pause(true);
  };

  private onVisibility = (): void => {
    if (document.hidden && (this.scene === "play" || this.scene === "tutorial")) this.pause(true);
  };

  private async toggleFullscreen(): Promise<void> {
    this.audio.play("button");
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen();
      const orientation = screen.orientation as { lock?: (mode: string) => Promise<void> } | undefined;
      await orientation?.lock?.("landscape")?.catch(() => undefined);
    } catch {
      return;
    }
  }

  private async requestPlayFullscreen(): Promise<void> {
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    if (coarse && !document.fullscreenElement) {
      try {
        await Promise.race([
          document.documentElement.requestFullscreen(),
          new Promise((resolve) => window.setTimeout(resolve, 400)),
        ]);
      } catch {
        return;
      }
      const orientation = screen.orientation as { lock?: (mode: string) => Promise<void> } | undefined;
      await orientation?.lock?.("landscape")?.catch(() => undefined);
    }
  }

  private async install(): Promise<void> {
    if (!shouldListenForInstallPrompt(PWA_ENABLED) || !this.installEvent) return;
    await this.installEvent.prompt();
    this.installEvent = null;
    this.syncInstallUi();
    this.ui.render();
  }
}

export type { DifficultyId };
